/**
 * Anvilcore PDP interactions.
 *
 * Everything here uses event delegation attached once to `document`, so it
 * keeps working after the Shopify Theme Editor adds, removes, reorders, or
 * re-renders any of the anvilcore-* sections — no re-init step is required.
 * Variant selection itself needs no JS: plan cards are real
 * `<input type="radio" name="id">` elements inside the native product form,
 * so the browser handles selection/submission on its own. This file only
 * handles presentational state (active thumbnail, selected card styling,
 * quantity stepper).
 */
(function () {
  if (window.__acPdpInit) return;
  window.__acPdpInit = true;

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  // Shared helper: swap the gallery's main stage image, and (if a media id
  // is given) sync the matching thumbnail's active state. Used both when a
  // thumbnail is clicked directly and when a plan/variant selection changes
  // the current variant's image.
  function setStageImage(gallery, opts) {
    if (!gallery || !opts || !opts.src) return;
    var stageImg = gallery.querySelector('[data-ac-stage-img]');
    if (stageImg) {
      stageImg.src = opts.src;
      if (opts.srcset) {
        stageImg.srcset = opts.srcset;
      } else {
        stageImg.removeAttribute('srcset');
      }
      if (opts.alt) stageImg.alt = opts.alt;
      if (opts.fit) {
        stageImg.setAttribute('data-fit', opts.fit);
      }
    }

    if (opts.mediaId) {
      gallery.querySelectorAll('[data-ac-thumb]').forEach(function (t) {
        t.setAttribute('data-active', String(t.getAttribute('data-ac-media-id') === opts.mediaId));
      });
    }
  }

  // Gallery: click a thumbnail to swap the main stage image.
  document.addEventListener('click', function (event) {
    var thumb = closest(event.target, '[data-ac-thumb]');
    if (!thumb) return;
    var gallery = closest(thumb, '[data-ac-gallery]');
    if (!gallery) return;

    setStageImage(gallery, {
      src: thumb.getAttribute('data-ac-full'),
      srcset: thumb.getAttribute('data-ac-full-srcset'),
      alt: thumb.getAttribute('data-ac-alt'),
      mediaId: thumb.getAttribute('data-ac-media-id'),
      fit: thumb.getAttribute('data-ac-full-fit'),
    });
  });

  // Plan cards: reflect the checked radio's visual state, keep the real
  // hidden variant-id field (consumed by the native product form / theme
  // JS) in sync, and recompute the Add to Cart total.
  function formatMoney(scope, amount) {
    var currency = scope.getAttribute('data-ac-currency') || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency,
        trailingZeroDisplay: 'stripIfInteger',
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function syncPlanCards(scope) {
    var checked = null;
    scope.querySelectorAll('[data-ac-plan]').forEach(function (card) {
      var input = card.querySelector('input[type="radio"]');
      var isChecked = !!(input && input.checked);
      card.setAttribute('data-selected', String(isChecked));
      if (isChecked) checked = input;
    });

    if (!checked) return;

    var hiddenVariantInput = scope.querySelector('[data-ac-variant-input]');
    if (hiddenVariantInput && checked.getAttribute('data-variant-id')) {
      hiddenVariantInput.value = checked.getAttribute('data-variant-id');
      hiddenVariantInput.dispatchEvent(new Event('input', { bubbles: true }));
      hiddenVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Swap the gallery stage image to the selected variant's own image (if
    // it has one distinct from whatever is already showing).
    var gallery = scope.querySelector('[data-ac-gallery]');
    if (gallery && checked.getAttribute('data-image-src')) {
      setStageImage(gallery, {
        src: checked.getAttribute('data-image-src'),
        srcset: checked.getAttribute('data-image-srcset'),
        alt: checked.getAttribute('data-image-alt'),
        mediaId: checked.getAttribute('data-media-id'),
        fit: checked.getAttribute('data-image-fit'),
      });
    }

    // Price: read the selected variant's real price / compare-at price as
    // integer cents (set server-side from live product data) instead of
    // parsing a formatted money string, so this never breaks under a
    // locale's thousands/decimal separators. This is the single source of
    // truth for the main price display, the compare-at/savings display,
    // and the Add to Cart total below.
    var priceCents = parseInt(checked.getAttribute('data-price-cents'), 10);
    if (isNaN(priceCents)) priceCents = 0;
    var compareAtCents = parseInt(checked.getAttribute('data-compare-at-cents'), 10);
    if (isNaN(compareAtCents)) compareAtCents = 0;
    var hasDiscount = compareAtCents > priceCents;

    var priceEl = scope.querySelector('[data-ac-price]');
    if (priceEl) {
      priceEl.textContent = formatMoney(scope, priceCents / 100);
    }

    var compareEl = scope.querySelector('[data-ac-price-compare]');
    if (compareEl) {
      if (hasDiscount) {
        compareEl.textContent = formatMoney(scope, compareAtCents / 100);
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }

    var savingsEl = scope.querySelector('[data-ac-price-savings]');
    if (savingsEl) {
      if (hasDiscount) {
        savingsEl.textContent = 'Save ' + formatMoney(scope, (compareAtCents - priceCents) / 100);
        savingsEl.classList.remove('hidden');
      } else {
        savingsEl.textContent = '';
        savingsEl.classList.add('hidden');
      }
    }

    var qtyInput = scope.querySelector('[data-ac-qty] input[type="number"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var totalEl = scope.querySelector('[data-ac-total]');
    if (totalEl) {
      totalEl.textContent = formatMoney(scope, (priceCents / 100) * qty);
    }
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (input.matches && input.matches('[data-ac-plan] input[type="radio"]')) {
      var group = closest(input, '[data-ac-buybox]');
      if (group) syncPlanCards(group);
      return;
    }
    if (input.matches && input.matches('[data-ac-qty] input[type="number"]')) {
      var buybox = closest(input, '[data-ac-buybox]');
      if (buybox) syncPlanCards(buybox);
    }
  });

  document.querySelectorAll('[data-ac-buybox]').forEach(syncPlanCards);

  // Quantity stepper: -/+ buttons drive the native number input.
  document.addEventListener('click', function (event) {
    var btn = closest(event.target, '[data-ac-qty-step]');
    if (!btn) return;
    var wrapper = closest(btn, '[data-ac-qty]');
    if (!wrapper) return;
    var input = wrapper.querySelector('input[type="number"]');
    if (!input) return;

    var step = parseInt(input.step || '1', 10) || 1;
    var min = parseInt(input.min || '1', 10) || 1;
    var max = input.max ? parseInt(input.max, 10) : Infinity;
    var current = parseInt(input.value || '1', 10) || 1;
    var next = btn.getAttribute('data-ac-qty-step') === 'up' ? current + step : current - step;
    next = Math.max(min, Math.min(max, next));

    if (next !== current) {
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // Gallery thumbnail pagination: page through thumbnails beyond the first
  // 8 using the prev/next arrows, without disturbing the existing grid.
  function updateThumbsPage(thumbsRow, page) {
    if (!thumbsRow) return;
    var perPage = parseInt(thumbsRow.getAttribute('data-ac-thumbs-per-page'), 10) || 8;
    var buttons = thumbsRow.querySelectorAll('[data-ac-thumb]');
    if (!buttons.length) return;
    var totalPages = Math.max(1, Math.ceil(buttons.length / perPage));
    page = Math.max(0, Math.min(page, totalPages - 1));

    buttons.forEach(function (btn) {
      var btnPage = parseInt(btn.getAttribute('data-ac-thumb-page'), 10) || 0;
      if (btnPage === page) {
        btn.removeAttribute('hidden');
      } else {
        btn.setAttribute('hidden', '');
      }
    });

    thumbsRow.setAttribute('data-ac-thumbs-page', String(page));

    var prevBtn = thumbsRow.querySelector('[data-ac-thumbs-nav="prev"]');
    var nextBtn = thumbsRow.querySelector('[data-ac-thumbs-nav="next"]');
    if (prevBtn) prevBtn.disabled = page <= 0;
    if (nextBtn) nextBtn.disabled = page >= totalPages - 1;

    var countEl = thumbsRow.querySelector('[data-ac-thumbs-count]');
    if (countEl) countEl.textContent = (page + 1) + ' / ' + totalPages;
  }

  function initThumbsPaging(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-ac-thumbs]').forEach(function (thumbsRow) {
      updateThumbsPage(thumbsRow, 0);
    });
  }

  document.addEventListener('click', function (event) {
    var navBtn = closest(event.target, '[data-ac-thumbs-nav]');
    if (!navBtn) return;
    var thumbsRow = closest(navBtn, '[data-ac-thumbs]');
    if (!thumbsRow) return;
    var currentPage = parseInt(thumbsRow.getAttribute('data-ac-thumbs-page'), 10) || 0;
    var direction = navBtn.getAttribute('data-ac-thumbs-nav') === 'next' ? 1 : -1;
    updateThumbsPage(thumbsRow, currentPage + direction);
  });

  initThumbsPaging(document);

  // Main stage arrows: step to the previous/next image in the full gallery
  // (not just the current thumbnail page), wrapping around at the ends, and
  // bring the corresponding thumbnail's page into view so the highlighted
  // thumbnail is never hidden behind the pagination.
  function navigateStage(gallery, direction) {
    if (!gallery) return;
    var thumbs = gallery.querySelectorAll('[data-ac-thumb]');
    if (!thumbs.length) return;

    var currentIndex = 0;
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].getAttribute('data-active') === 'true') {
        currentIndex = i;
        break;
      }
    }
    var nextIndex = (currentIndex + direction + thumbs.length) % thumbs.length;
    var target = thumbs[nextIndex];

    setStageImage(gallery, {
      src: target.getAttribute('data-ac-full'),
      srcset: target.getAttribute('data-ac-full-srcset'),
      alt: target.getAttribute('data-ac-alt'),
      mediaId: target.getAttribute('data-ac-media-id'),
      fit: target.getAttribute('data-ac-full-fit'),
    });

    var thumbsRow = gallery.querySelector('[data-ac-thumbs]');
    if (thumbsRow) {
      var targetPage = parseInt(target.getAttribute('data-ac-thumb-page'), 10) || 0;
      updateThumbsPage(thumbsRow, targetPage);
    }
  }

  document.addEventListener('click', function (event) {
    var stageNavBtn = closest(event.target, '[data-ac-stage-nav]');
    if (!stageNavBtn) return;
    var gallery = closest(stageNavBtn, '[data-ac-gallery]');
    if (!gallery) return;
    var direction = stageNavBtn.getAttribute('data-ac-stage-nav') === 'next' ? 1 : -1;
    navigateStage(gallery, direction);
  });

  // Re-sync plan cards whenever the Theme Editor swaps a section back in.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target && target.querySelectorAll) {
      target.querySelectorAll('[data-ac-buybox]').forEach(syncPlanCards);
      initThumbsPaging(target);
    }
  });

  // ---------------------------------------------------------------------
  // Kaching Bundles price sync.
  // When Kaching's bundle widget is rendered in the buy box (the section's
  // "Use app purchase options" mode), the theme's own plan cards are
  // hidden, so the main price at the top of the buy box would otherwise
  // stay frozen at the variant price. Mirror the currently selected
  // Kaching bar's price / compare-at price into the main price display,
  // and restore the server-rendered defaults when no bar is selected.
  // ---------------------------------------------------------------------
  function parseMoneyToCents(text) {
    if (!text) return NaN;
    var m = String(text).replace(/ /g, ' ').match(/\d[\d.,\s]*/);
    if (!m) return NaN;
    var s = m[0].replace(/\s/g, '').replace(/[.,]+$/, '');
    var lastSep = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
    var normalized;
    if (lastSep > -1 && s.length - lastSep - 1 <= 2) {
      normalized = s.slice(0, lastSep).replace(/[.,]/g, '') + '.' + s.slice(lastSep + 1);
    } else {
      normalized = s.replace(/[.,]/g, '');
    }
    var value = parseFloat(normalized);
    return isNaN(value) ? NaN : Math.round(value * 100);
  }

  function formatMoneyFixed(scope, cents) {
    var currency = scope.getAttribute('data-ac-currency') || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function syncKachingPrice(buybox) {
    var host = buybox.querySelector('.ac-app-purchase-options');
    var priceEl = buybox.querySelector('[data-ac-price]');
    if (!host || !priceEl) return;
    var compareEl = buybox.querySelector('[data-ac-price-compare]');
    var savingsEl = buybox.querySelector('[data-ac-price-savings]');

    // Remember the server-rendered defaults once, before we change anything.
    if (!priceEl.hasAttribute('data-ac-default-price')) {
      priceEl.setAttribute('data-ac-default-price', priceEl.textContent);
      if (compareEl) {
        compareEl.setAttribute('data-ac-default-text', compareEl.textContent);
        compareEl.setAttribute('data-ac-default-hidden', String(compareEl.classList.contains('hidden')));
      }
      if (savingsEl) {
        savingsEl.setAttribute('data-ac-default-text', savingsEl.textContent);
        savingsEl.setAttribute('data-ac-default-hidden', String(savingsEl.classList.contains('hidden')));
      }
    }

    function restore(el) {
      if (!el) return;
      el.textContent = el.getAttribute('data-ac-default-text') || '';
      el.classList.toggle('hidden', el.getAttribute('data-ac-default-hidden') === 'true');
    }

    var bar = host.querySelector('.kaching-bundles__bar--selected');
    var barPriceEl = bar && bar.querySelector('.kaching-bundles__bar-price');
    var priceText = barPriceEl ? barPriceEl.textContent.replace(/\s+/g, ' ').trim() : '';
    var priceCents = parseMoneyToCents(priceText);

    if (!bar || !priceText || isNaN(priceCents)) {
      priceEl.textContent = priceEl.getAttribute('data-ac-default-price');
      restore(compareEl);
      restore(savingsEl);
      return;
    }

    priceEl.textContent = priceText;

    var fullEl = bar.querySelector('.kaching-bundles__bar-full-price');
    var fullText = fullEl ? fullEl.textContent.replace(/\s+/g, ' ').trim() : '';
    var fullCents = parseMoneyToCents(fullText);
    var hasDiscount = !isNaN(fullCents) && fullCents > priceCents;

    if (compareEl) {
      compareEl.textContent = hasDiscount ? fullText : '';
      compareEl.classList.toggle('hidden', !hasDiscount);
    }
    if (savingsEl) {
      savingsEl.textContent = hasDiscount ? 'Save ' + formatMoneyFixed(buybox, fullCents - priceCents) : '';
      savingsEl.classList.toggle('hidden', !hasDiscount);
    }
  }

  function scheduleKachingSync(buybox) {
    if (buybox.__acKachingRaf) return;
    buybox.__acKachingRaf = requestAnimationFrame(function () {
      buybox.__acKachingRaf = 0;
      syncKachingPrice(buybox);
    });
  }

  function initKachingSync(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-ac-buybox]').forEach(function (buybox) {
      var host = buybox.querySelector('.ac-app-purchase-options');
      if (!host || host.__acKachingObserved) return;
      host.__acKachingObserved = true;
      new MutationObserver(function () {
        scheduleKachingSync(buybox);
      }).observe(host, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      scheduleKachingSync(buybox);
    });
  }

  document.addEventListener('change', function (event) {
    var t = event.target;
    if (!t || !t.matches || !t.matches('.kaching-bundles__bar input')) return;
    var buybox = closest(t, '[data-ac-buybox]');
    if (buybox) scheduleKachingSync(buybox);
  });

  initKachingSync(document);
  document.addEventListener('shopify:section:load', function (event) {
    initKachingSync(event.target);
  });
})();
